import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import ClientiDaRichiamare from "@/components/ClientiDaRichiamare";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { 
  Bell,
  Mail,
  MessageSquare,
  Calendar,
  Users,
  Play,
  Settings,
  Clock,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Gift,
  RefreshCw,
  Send,
  Edit,
  Eye,
  Zap,
  Sparkles
} from "lucide-react";

export default function MarketingAutomazione() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [campagne, setCampagne] = useState([]);
  const [invii, setInvii] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [esecuzioneInCorso, setEsecuzioneInCorso] = useState(false);
  
  // Dialog
  const [dialogModificaCampagna, setDialogModificaCampagna] = useState(false);
  const [campagnaSelezionata, setCampagnaSelezionata] = useState(null);
  const [templateModificato, setTemplateModificato] = useState("");
  const [oggettoModificato, setOggettoModificato] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [campagneRes, inviiRes, dashboardRes] = await Promise.all([
        axios.get(`${API}/marketing/campagne`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/marketing/invii?limit=50`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/marketing/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCampagne(campagneRes.data || []);
      setInvii(inviiRes.data || []);
      setDashboard(dashboardRes.data || null);
    } catch (error) {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCampagna = async (campagnaId, attiva) => {
    try {
      await axios.put(`${API}/marketing/campagne/${campagnaId}?attiva=${attiva}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(attiva ? "Campagna attivata" : "Campagna disattivata");
      setTimeout(() => fetchData(), 100);
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleSalvaTemplate = async () => {
    if (!campagnaSelezionata) return;
    
    try {
      await axios.put(
        `${API}/marketing/campagne/${campagnaSelezionata.id}?template_messaggio=${encodeURIComponent(templateModificato)}&oggetto_email=${encodeURIComponent(oggettoModificato)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Template aggiornato");
      setDialogModificaCampagna(false);
      setTimeout(() => fetchData(), 150);
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleEseguiCampagne = async () => {
    setEsecuzioneInCorso(true);
    try {
      const res = await axios.post(`${API}/marketing/campagne/esegui`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const risultati = res.data.risultati;
      const totale = (risultati.recall?.inviati || 0) + (risultati.compleanno?.inviati || 0) + (risultati.promemoria?.inviati || 0);
      
      if (totale > 0) {
        toast.success(`Campagne eseguite: ${totale} messaggi in coda`);
      } else {
        toast.info("Nessun messaggio da inviare oggi");
      }
      setTimeout(() => fetchData(), 100);
    } catch (error) {
      toast.error("Errore nell'esecuzione");
    } finally {
      setEsecuzioneInCorso(false);
    }
  };

  const getCampagnaIcon = (tipo) => {
    switch (tipo) {
      case 'recall': return <RefreshCw className="w-5 h-5" />;
      case 'compleanno': return <Gift className="w-5 h-5" />;
      case 'promemoria_appuntamento': return <Calendar className="w-5 h-5" />;
      default: return <Bell className="w-5 h-5" />;
    }
  };

  const getCampagnaColor = (tipo) => {
    switch (tipo) {
      case 'recall': return 'bg-blue-100 text-blue-700';
      case 'compleanno': return 'bg-pink-100 text-pink-700';
      case 'promemoria_appuntamento': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="marketing-automazione-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Zap className="w-7 h-7 text-amber-500" />
            Automazione Marketing
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Configura e gestisci le campagne automatiche
          </p>
        </div>
        <Button
          onClick={handleEseguiCampagne}
          disabled={esecuzioneInCorso}
          className="bg-amber-500 hover:bg-amber-600"
        >
          {esecuzioneInCorso ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Play className="w-4 h-4 mr-2" />
          )}
          Esegui Campagne Ora
        </Button>
      </div>

      {/* Dashboard KPI */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-green-600 font-medium">Attivi (&lt;30g)</p>
                  <p className="text-2xl font-bold text-green-800">{dashboard.kpi?.clienti_attivi_30g || 0}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-amber-600 font-medium">Da Richiamare (30-60g)</p>
                  <p className="text-2xl font-bold text-amber-800">{dashboard.kpi?.clienti_da_richiamare_60g || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-orange-600 font-medium">A Rischio (60-90g)</p>
                  <p className="text-2xl font-bold text-orange-800">{dashboard.kpi?.clienti_a_rischio_90g || 0}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-orange-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-red-600 font-medium">Persi (&gt;90g)</p>
                  <p className="text-2xl font-bold text-red-800">{dashboard.kpi?.clienti_persi || 0}</p>
                </div>
                <Users className="w-8 h-8 text-red-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-blue-600 font-medium">Tasso Fidelizzazione</p>
                  <p className="text-2xl font-bold text-blue-800">{dashboard.metriche?.tasso_fidelizzazione || 0}%</p>
                </div>
                <TrendingUp className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="richiamare" className="space-y-4">
        <TabsList>
          <TabsTrigger value="richiamare" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Da Richiamare
          </TabsTrigger>
          <TabsTrigger value="campagne" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Campagne ({campagne.length})
          </TabsTrigger>
          <TabsTrigger value="storico" className="flex items-center gap-2">
            <Send className="w-4 h-4" />
            Storico Invii ({invii.length})
          </TabsTrigger>
        </TabsList>

        {/* Tab Clienti da Richiamare */}
        <TabsContent value="richiamare">
          <ClientiDaRichiamare 
            onInviaMessaggio={(cliente, servizio) => {
              // Apri WhatsApp con messaggio precompilato
              const messaggio = `Ciao ${cliente.nome}! Ti ricordiamo che è passato un po' di tempo dal tuo ultimo ${servizio.toLowerCase()}. Ti aspettiamo per un nuovo appuntamento! - I Cirillo Parrucchieri`;
              const telefono = cliente.telefono?.replace(/\D/g, '');
              if (telefono) {
                window.open(`https://wa.me/39${telefono}?text=${encodeURIComponent(messaggio)}`, '_blank');
              } else {
                toast.error("Numero di telefono non disponibile");
              }
            }}
          />
        </TabsContent>

        {/* Tab Campagne */}
        <TabsContent value="campagne">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {campagne.map(campagna => (
              <Card key={campagna.id} className={`border-l-4 ${campagna.attiva ? 'border-l-green-500' : 'border-l-slate-300'}`}>
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getCampagnaColor(campagna.tipo)}`}>
                        {getCampagnaIcon(campagna.tipo)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800">{campagna.nome}</h3>
                        <Badge variant="outline" className={getCampagnaColor(campagna.tipo)}>
                          {campagna.tipo === 'recall' ? 'Recall' : 
                           campagna.tipo === 'compleanno' ? 'Compleanno' : 'Promemoria'}
                        </Badge>
                      </div>
                    </div>
                    <Switch
                      checked={campagna.attiva}
                      onCheckedChange={(checked) => handleToggleCampagna(campagna.id, checked)}
                    />
                  </div>
                  
                  <div className="space-y-2 text-sm text-slate-600 mb-4">
                    {campagna.tipo === 'recall' && (
                      <p className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-slate-400" />
                        Clienti assenti da <strong>{campagna.giorni_assenza}</strong> giorni
                      </p>
                    )}
                    {campagna.tipo === 'promemoria_appuntamento' && (
                      <p className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <strong>{campagna.giorni_prima_appuntamento}</strong> giorno prima dell'appuntamento
                      </p>
                    )}
                    <p className="flex items-center gap-2">
                      {campagna.canale_email && <Mail className="w-4 h-4 text-blue-500" />}
                      {campagna.canale_whatsapp && <MessageSquare className="w-4 h-4 text-green-500" />}
                      Canali: {campagna.canale_email ? 'Email' : ''} {campagna.canale_whatsapp ? 'WhatsApp' : ''}
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t">
                    <div className="text-xs text-slate-500">
                      <span>Invii totali: <strong>{campagna.invii_totali || 0}</strong></span>
                      {campagna.ultimo_invio && (
                        <span className="ml-3">
                          Ultimo: {format(new Date(campagna.ultimo_invio), 'dd/MM/yyyy', { locale: it })}
                        </span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setCampagnaSelezionata(campagna);
                        setTemplateModificato(campagna.template_messaggio || '');
                        setOggettoModificato(campagna.oggetto_email || '');
                        setDialogModificaCampagna(true);
                      }}
                    >
                      <Edit className="w-3.5 h-3.5 mr-1" />
                      Modifica
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Info Box */}
          <Card className="mt-4 bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-800">Come funziona?</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Le campagne attive vengono eseguite quando clicchi "Esegui Campagne Ora". 
                    In produzione, puoi configurare un job automatico (cron) per eseguirle ogni giorno.
                    I messaggi vengono messi in coda e inviati tramite <strong>SendGrid</strong> (email) o <strong>Twilio</strong> (WhatsApp).
                  </p>
                  <div className="mt-3 flex gap-2">
                    <Badge variant="outline" className="bg-white">
                      <Mail className="w-3 h-3 mr-1" />
                      SendGrid per Email
                    </Badge>
                    <Badge variant="outline" className="bg-white">
                      <MessageSquare className="w-3 h-3 mr-1" />
                      Twilio per WhatsApp
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Storico Invii */}
        <TabsContent value="storico">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Storico Invii Recenti</CardTitle>
            </CardHeader>
            <CardContent>
              {invii.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Send className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg">Nessun invio registrato</p>
                  <p className="text-sm mt-1">Attiva le campagne e clicca "Esegui" per iniziare</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {invii.map(invio => (
                    <div 
                      key={invio.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getCampagnaColor(invio.tipo)}`}>
                          {getCampagnaIcon(invio.tipo)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-800">{invio.cliente_nome}</span>
                            <Badge variant="outline" className="text-xs">
                              {invio.campagna_nome}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <Mail className="w-3 h-3" />
                            <span>{invio.email}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Badge className={
                          invio.stato === 'inviato' ? 'bg-green-100 text-green-700' :
                          invio.stato === 'in_coda' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }>
                          {invio.stato === 'inviato' ? 'Inviato' :
                           invio.stato === 'in_coda' ? 'In Coda' : 'Errore'}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          {invio.data_invio ? format(new Date(invio.data_invio), 'dd/MM HH:mm', { locale: it }) : '-'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Modifica Template */}
      <Dialog open={dialogModificaCampagna} onOpenChange={setDialogModificaCampagna}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Modifica Template: {campagnaSelezionata?.nome}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Oggetto Email</Label>
              <Input
                value={oggettoModificato}
                onChange={(e) => setOggettoModificato(e.target.value)}
                placeholder="Oggetto dell'email..."
              />
            </div>
            <div>
              <Label>Template Messaggio</Label>
              <Textarea
                value={templateModificato}
                onChange={(e) => setTemplateModificato(e.target.value)}
                rows={8}
                placeholder="Scrivi il template del messaggio..."
              />
              <p className="text-xs text-slate-500 mt-2">
                Variabili disponibili: <code>{'{nome_cliente}'}</code>, <code>{'{nome_salone}'}</code>, 
                <code>{'{giorni_assenza}'}</code>, <code>{'{data_appuntamento}'}</code>, <code>{'{ora_appuntamento}'}</code>, <code>{'{servizi}'}</code>
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setDialogModificaCampagna(false)}>Annulla</Button>
              <Button onClick={handleSalvaTemplate} className="bg-blue-600 hover:bg-blue-700">
                Salva Template
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
